import React, { useEffect, useMemo, useRef } from 'react';
import Checkbox from '../../../components/common/Checkbox/Checkbox';
import { AppFormInput, AppFormSearchableSelect } from '../../../components/common/appForms';
import CountryFlag from '../../../components/common/CountryFlag/CountryFlag';
import type { SelectOption } from '../../../components/common/SearchableSelect/SearchableSelect';
import { CALLING_CODES, CODE_TO_ISO, extractCallingCode } from '../../../constants/callingCodes';
import { maskPhoneByCountry, phoneMaxLength, phonePlaceholder } from '../../../utils/masks';
import type { CheckoutPayerData } from '../checkout.types';
import { maskCheckoutPayerField } from '../checkout.utils';
import pageStyles from '../CheckoutPage.module.css';
import styles from './CheckoutShared.module.css';

interface CreditCardPayerFieldsProps {
  data: CheckoutPayerData;
  useStudentDataAsPayer: boolean;
  errors: Record<string, string>;
  disabled?: boolean;
  /** No checkout resumido não há formulário da aluna acima. */
  hideUseStudentDataToggle?: boolean;
  onUseStudentDataChange: (useStudentData: boolean) => void;
  onFieldChange: <K extends keyof CheckoutPayerData>(
    field: K,
    value: CheckoutPayerData[K],
  ) => void;
}

const sortWithBrazilFirst = <T extends { value: string; label: string }>(
  options: T[],
  brazilValue = '55',
): T[] => {
  const sorted = [...options].sort((left, right) => left.label.localeCompare(right.label, 'pt'));
  const brazilIndex = sorted.findIndex((option) => option.value === brazilValue);

  if (brazilIndex > 0) {
    const [brazilOption] = sorted.splice(brazilIndex, 1);
    sorted.unshift(brazilOption);
  }

  return sorted;
};

const formatCallingCodeTrigger = (option: SelectOption) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
    <CountryFlag code={CODE_TO_ISO[option.value] || ''} size={16} />
    +{extractCallingCode(option.value)}
  </span>
);

const formatCallingCodeOption = (option: SelectOption) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
    <CountryFlag code={CODE_TO_ISO[option.value] || ''} size={16} />
    {option.label}
  </span>
);

const CreditCardPayerFields: React.FC<CreditCardPayerFieldsProps> = ({
  data,
  useStudentDataAsPayer,
  errors,
  disabled = false,
  hideUseStudentDataToggle = false,
  onUseStudentDataChange,
  onFieldChange,
}) => {
  const prevCallingCode = useRef(data.callingCode);

  const callingCodeOptions = useMemo(
    () => sortWithBrazilFirst(CALLING_CODES).map((entry) => ({
      value: entry.value,
      label: entry.label,
    })),
    [],
  );

  useEffect(() => {
    if (useStudentDataAsPayer || prevCallingCode.current === data.callingCode) {
      return;
    }

    prevCallingCode.current = data.callingCode;
    onFieldChange('phone', maskPhoneByCountry(data.phone, data.callingCode));
  }, [data.callingCode, data.phone, onFieldChange, useStudentDataAsPayer]);

  const handleChange = (field: keyof CheckoutPayerData, rawValue: string) => {
    onFieldChange(field, maskCheckoutPayerField(field, rawValue, data.callingCode) as CheckoutPayerData[typeof field]);
  };

  return (
    <div className={styles.creditCardFields}>
      <h3 className={styles.payerSectionTitle}>Dados do pagador</h3>
      <p className={styles.payerSectionHint}>
        Titular do cartão para o Asaas. O Asaas só aceita CPF e CEP brasileiros.
        Se a aluna mora no exterior, use os dados de quem paga no Brasil.
      </p>

      {!hideUseStudentDataToggle && (
        <Checkbox
          variant="classic"
          compact
          checked={useStudentDataAsPayer}
          disabled={disabled}
          onPress={() => onUseStudentDataChange(!useStudentDataAsPayer)}
          className={styles.payerSameDataCheckbox}
          boxClassName={styles.payerSameDataCheckboxBox}
          labelClassName={styles.payerSameDataCheckboxLabel}
          label="Usar os mesmos dados preenchidos acima"
          ariaLabel="Usar os mesmos dados preenchidos acima"
        />
      )}

      {(!useStudentDataAsPayer || hideUseStudentDataToggle) && (
        <>
          <AppFormInput
            id="checkout-payer-name"
            name="payerName"
            label="Nome do titular do cartão"
            value={data.name}
            onChange={(event) => handleChange('name', event.target.value)}
            placeholder="Nome do titular do cartão"
            error={errors.payerName}
            disabled={disabled}
            autoComplete="name"
          />

          <AppFormInput
            id="checkout-payer-email"
            name="payerEmail"
            label="E-mail"
            type="email"
            value={data.email}
            onChange={(event) => handleChange('email', event.target.value)}
            placeholder="email@exemplo.com"
            error={errors.payerEmail}
            disabled={disabled}
            autoComplete="email"
          />

          <AppFormInput
            id="checkout-payer-cpf"
            name="payerCpf"
            label="CPF"
            value={data.cpf}
            onChange={(event) => handleChange('cpf', event.target.value)}
            placeholder="000.000.000-00"
            inputMode="numeric"
            error={errors.payerCpf}
            disabled={disabled}
            autoComplete="off"
          />

          <div className={pageStyles.fieldRowHalf}>
            <AppFormInput
              id="checkout-payer-postal-code"
              name="payerPostalCode"
              label="CEP"
              value={data.postalCode}
              onChange={(event) => handleChange('postalCode', event.target.value)}
              placeholder="00000-000"
              inputMode="numeric"
              error={errors.payerPostalCode}
              disabled={disabled}
              autoComplete="postal-code"
              containerClassName={pageStyles.fieldRowItem}
            />

            <AppFormInput
              id="checkout-payer-address-number"
              name="payerAddressNumber"
              label="Número"
              value={data.addressNumber}
              onChange={(event) => handleChange('addressNumber', event.target.value)}
              placeholder="Nº"
              error={errors.payerAddressNumber}
              disabled={disabled}
              autoComplete="address-line2"
              containerClassName={pageStyles.fieldRowItem}
            />
          </div>

          <AppFormInput
            id="checkout-payer-address-complement"
            name="payerAddressComplement"
            label="Complemento"
            value={data.addressComplement}
            onChange={(event) => handleChange('addressComplement', event.target.value)}
            placeholder="Opcional"
            error={errors.payerAddressComplement}
            disabled={disabled}
            autoComplete="address-line3"
          />

          <div className={pageStyles.fieldRowHalf}>
            <AppFormSearchableSelect
              id="checkout-payer-calling-code"
              name="payerCallingCode"
              label="DDI"
              options={callingCodeOptions}
              value={data.callingCode || '55'}
              onChange={(event) => onFieldChange('callingCode', event.target.value)}
              placeholder="Brasil (+55)"
              searchPlaceholder="Pesquisar"
              noResultsText="Nenhum código encontrado"
              disabled={disabled}
              allowEmpty={false}
              triggerFormat={formatCallingCodeTrigger}
              optionFormat={formatCallingCodeOption}
              containerClassName={pageStyles.fieldRowItem}
              error={errors.payerCallingCode}
            />

            <AppFormInput
              id="checkout-payer-phone"
              name="payerPhone"
              label="Telefone"
              value={data.phone}
              onChange={(event) => handleChange('phone', event.target.value)}
              placeholder={phonePlaceholder(data.callingCode)}
              inputMode="tel"
              maxLength={phoneMaxLength(data.callingCode)}
              error={errors.payerPhone}
              disabled={disabled}
              autoComplete="tel"
              containerClassName={pageStyles.fieldRowItem}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default CreditCardPayerFields;
